import PropTypes from 'prop-types';

import Container, { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { useGetPageContentQuery } from '../../services/seoContent';
import { CircularProgress, windowExists } from '@classity-edtech/ui-base';
import Helmet from '../../components/Helmet';
import Header from '../../components/SEO/Header';
import { Footer } from '../../components/SEO/Footer';
import { Hero } from '../../components/SEO/Hero';
import { Pitch } from '../../components/SEO/Pitch';
import { Features } from '../../components/SEO/Features';
import { Company } from '../../components/SEO/Company';
import { extractFooterContent, extractHeaderContent } from '../../utils/extractContents';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';

export interface AboutProps extends MuiContainerProps {}

export const About = ({ className }: AboutProps) => {
  const screens = useAppMediaQuery();

  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'about_us',
    contentType: 'page',
  });

  const extractContent = () => {
    if (!!data) {
      return {
        seo: {
          title: data?.seo?.fields?.titleTag,
          keywords: data?.seo?.fields?.keywords,
          description: data?.seo?.fields?.metaDescription,
          metaRobotsIndex: data?.seo?.fields?.metaRobotsIndex,
        },
      };
    }
  };

  const extractHeroContent = () => {
    const content = data?.sections?.[0];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      heroImage: content?.fields?.list?.[0]?.fields?.media?.fields?.image?.fields?.file?.url ?? '',
    };
  };

  const extractTeachingSection = () => {
    const content = data?.sections?.[1];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      items:
        content?.fields?.list?.map((item: any) => ({
          title: item?.fields?.title ?? '',
          description: item?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
          imageUrl: item?.fields?.media?.fields?.icon?.fields?.file?.url ?? '',
        })) ?? [],
    };
  };

  const extractCompanyHistory = () => {
    const fields = data?.sections?.[2]?.fields;

    const title = fields?.title ?? '';

    const description = fields?.description?.content?.[0]?.content?.[0]?.value ?? '';
    const founderImage = fields?.list?.[1]?.fields?.media?.fields?.image?.fields?.file?.url ?? '';

    const list =
      fields?.description?.content
        ?.slice(1)
        ?.map(item => item?.content?.[0]?.value)
        ?.filter(Boolean) ?? [];

    // 4. Testimony
    const testimonyBlock = fields?.list?.[0]?.fields;
    const testimonyContent = testimonyBlock?.description?.content ?? [];

    const testimony = {
      testimony: testimonyContent?.[0]?.content?.[0]?.value ?? '',
      name: testimonyContent?.[1]?.content?.[0]?.value ?? '',
      title: testimonyContent?.[2]?.content?.[0]?.value ?? '',
      cta: testimonyBlock?.reference?.fields?.label ?? '',
      ctaLink: testimonyBlock?.reference?.fields?.url ?? '',
    };

    return {
      title,
      description,
      list,
      testimony,
      imageUrl: founderImage,
    };
  };

  const extractClassityHighlights = () => {
    const content = data?.sections?.[3];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      items:
        content?.fields?.list?.map((item: any) => ({
          title: item?.fields?.title ?? '',
          descriptions:
            item?.fields?.description?.content?.map(
              (block: any) => block?.content?.[0]?.value ?? '',
            ) ?? [],
          cta: item?.fields?.reference?.fields?.label ?? '',
          imageUrl: item?.fields?.media?.fields?.image?.fields?.file?.url ?? '',
        })) ?? [],
    };
  };

  const extractPitchSection = () => {
    const content = data?.sections?.[4];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      titleImageUrl:
        content?.fields?.list?.[content?.fields?.list?.length - 1]?.fields?.media?.fields?.icon
          ?.fields?.file?.url ?? '',
      items:
        content?.fields?.list?.slice(0, content?.fields?.list?.length - 1)?.map((item: any) => ({
          title: item?.fields?.title ?? '',
          description: item?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
          imageUrl: item?.fields?.media?.fields?.icon?.fields?.file?.url ?? '',
        })) ?? [],
    };
  };

  return (
    <>
      <Helmet
        title={extractContent()?.seo?.title}
        keywords={extractContent()?.seo?.keywords}
        description={extractContent()?.seo?.description}
        metaRobotsIndex={extractContent()?.seo?.metaRobotsIndex}
      />

      {fetchingContent ? (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Header isExpanded {...extractHeaderContent(data)} hideNavigation={true} />

          <Hero hideCta {...extractHeroContent()} isSmallerHeader />

          <Box sx={{ height: '72px' }} />

          <Pitch {...extractTeachingSection()} isSmallerHeader />

          <Box sx={{ height: '64px' }} />

          <Box id='main-content' sx={{ height: '8px' }}></Box>

          <Company {...extractCompanyHistory()} />

          {false && (
            <Features isExecutives showLinkedInLink contents={extractClassityHighlights()} />
          )}

          <Pitch
            background={globalColours['$global-colour-palette-gray-50']}
            pb={9}
            hideArrow
            showTopImage
            isSmallerHeader
            {...extractPitchSection()}
          />

          <Footer {...extractFooterContent(data)} />
        </>
      )}
    </>
  );
};

About.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

About.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(About)(
  ({ theme }) => `
`,
);
